import { useState } from "react";
import { Alert, Button, Drawer, Popconfirm, Switch, Table, Tag } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useScopeParams } from "@/hooks/useScopeParams";
import ruleStudioApi from "@/services/ruleStudioApi";
import type { ReviewRuleConfig, RuleRun } from "./ruleStudio.types";

const REVIEW_KINDS = ["review_materiality", "review_sla"];

const STATUS_COLORS: Record<string, string> = {
  active: "green",
  draft: "default",
  paused: "orange",
};

const TRIGGER_LABELS: Record<string, string> = {
  manual: "Manual",
  activation: "Manual (activation)",
  data_refresh: "Data refresh",
};

type RuleDetailDrawerProps = {
  ruleId: number | null;
  onClose: () => void;
  onChanged: () => void;
};

/** Detail drawer for a custom rule: locked SQL, run history, and actions. */
export default function RuleDetailDrawer({
  ruleId,
  onClose,
  onChanged,
}: RuleDetailDrawerProps) {
  const { id } = useScopeParams();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);

  const queryKey = ["rule-studio-rule", id, ruleId];
  const { data: rule, isLoading } = useQuery({
    queryKey,
    queryFn: () => ruleStudioApi.getRule(id, ruleId as number),
    enabled: ruleId !== null,
  });

  const isReviewRule = REVIEW_KINDS.includes(rule?.rule_kind ?? "");

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey });
    onChanged();
  };

  const runMutation = useMutation({
    mutationFn: () => ruleStudioApi.runRule(id, ruleId as number),
    onSuccess: () => {
      setActionError(null);
      refresh();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => ruleStudioApi.deleteRule(id, ruleId as number),
    onSuccess: () => {
      setActionError(null);
      onChanged();
      onClose();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const statusMutation = useMutation({
    mutationFn: (status: string) =>
      ruleStudioApi.patchRule(id, ruleId as number, { status }),
    onSuccess: () => {
      setActionError(null);
      refresh();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      ruleStudioApi.toggleRule(id, ruleId as number, enabled),
    onSuccess: () => {
      setActionError(null);
      refresh();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const runColumns = [
    {
      title: "Run",
      dataIndex: "started_at",
      render: (value: string) => new Date(value).toLocaleString(),
    },
    {
      title: "Trigger",
      dataIndex: "trigger",
      render: (value: string) => TRIGGER_LABELS[value] ?? value,
    },
    {
      title: "New",
      dataIndex: "new_count",
      align: "right" as const,
      render: (value: number, record: RuleRun) =>
        record.status === "error" ? (
          <Tag color="red">error</Tag>
        ) : value ? (
          <b>+{value}</b>
        ) : (
          "0"
        ),
    },
    {
      title: "Auto-resolved",
      dataIndex: "resolved_count",
      align: "right" as const,
      render: (value: number) => (value ? `−${value}` : "0"),
    },
    { title: "Still open", dataIndex: "open_count", align: "right" as const },
    {
      title: "Duration",
      dataIndex: "duration_ms",
      align: "right" as const,
      render: (value: number | null) => (value === null ? "-" : `${value} ms`),
    },
  ];

  return (
    <Drawer
      open={ruleId !== null}
      onClose={onClose}
      width={760}
      title={
        rule ? (
          <div className="flex items-center gap-2">
            <span>{rule.name}</span>
            <Tag color={STATUS_COLORS[rule.status]}>{rule.status}</Tag>
            <Tag color="blue">Custom</Tag>
          </div>
        ) : (
          "Rule"
        )
      }
      loading={isLoading}
    >
      {rule && (
        <div className="flex flex-col gap-4">
          <div className="text-xs text-gray-500">
            {rule.level}-level · {rule.domain ?? "-"} · {rule.severity} ·
            authored by {rule.created_by ?? "—"} · in tenant library
            {rule.enabled_trials.length > 1 &&
              ` · enabled on ${rule.enabled_trials.length} trials`}
          </div>

          <div className="flex items-center gap-3">
            {!isReviewRule && (
              <Button
                size="small"
                loading={runMutation.isPending}
                disabled={rule.status !== "active" || !rule.enabled_for_trial}
                onClick={() => runMutation.mutate()}
              >
                Run now
              </Button>
            )}
            {rule.status === "active" ? (
              <Button
                size="small"
                onClick={() => statusMutation.mutate("paused")}
                loading={statusMutation.isPending}
              >
                Pause rule
              </Button>
            ) : (
              <Popconfirm
                title="Activate this rule?"
                description="It will run against current trial data immediately after the next refresh or manual run."
                onConfirm={() => statusMutation.mutate("active")}
              >
                <Button size="small" loading={statusMutation.isPending}>
                  Activate rule
                </Button>
              </Popconfirm>
            )}
            <span className="flex items-center gap-2 text-xs text-gray-500 ml-auto">
              Enabled for this trial
              <Switch
                size="small"
                checked={rule.enabled_for_trial}
                loading={toggleMutation.isPending}
                onChange={(checked) => toggleMutation.mutate(checked)}
              />
            </span>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <span className="text-xs text-gray-600">
              Deleting retires the rule: it stops identifying new discrepancies.
              The <strong>{rule.open_count}</strong> it has already identified
              are kept, with their status, assignee and comments. The
              rule&apos;s SQL and run history are not recoverable — pause
              instead if you may want it back.
            </span>
            <Popconfirm
              title="Delete this rule?"
              description="It stops finding new discrepancies. Those it already found are kept. The rule itself cannot be recovered."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => deleteMutation.mutate()}
            >
              <Button size="small" danger loading={deleteMutation.isPending}>
                Delete rule
              </Button>
            </Popconfirm>
          </div>

          {actionError && (
            <Alert
              type="error"
              showIcon
              message={actionError}
              closable
              onClose={() => setActionError(null)}
            />
          )}

          {runMutation.data && (
            <Alert
              type="success"
              showIcon
              message={`Run complete — ${runMutation.data.new_count} new, ${runMutation.data.resolved_count} auto-resolved, ${runMutation.data.open_count} open (${runMutation.data.duration_ms} ms).`}
            />
          )}

          {isReviewRule ? (
            <ReviewRuleConfigView
              config={rule.config}
              version={rule.config_version}
            />
          ) : (
            <div>
              <div className="flex justify-between items-center text-[11px] text-gray-500 px-3 py-1.5 bg-gray-50 border border-b-0 border-gray-200 rounded-t-md">
                <span>
                  Locked logic · v{rule.sql_version}
                  {rule.sql_version > 1 &&
                    " · discrepancies keep the version that created them"}
                </span>
                <span>read-only · 30 s timeout</span>
              </div>
              <pre className="border border-gray-200 rounded-b-md p-3 text-xs font-mono overflow-x-auto max-h-[320px] overflow-y-auto whitespace-pre-wrap m-0">
                {rule.sql_text}
              </pre>
            </div>
          )}

          {rule.explanation && (
            <div className="rounded-md border border-[#BFDCEA] bg-[#EAF4F9] px-3 py-2 text-xs text-[#0B4A66]">
              <span className="font-semibold text-[#06709B]">
                How this works:
              </span>{" "}
              {rule.explanation}
            </div>
          )}

          <div>
            <p className="text-sm font-semibold text-[#003832] mb-2">
              Run history
            </p>
            <Table
              size="small"
              rowKey="id"
              columns={runColumns}
              dataSource={rule.runs ?? []}
              pagination={false}
              locale={{ emptyText: "No runs yet" }}
            />
            <p className="text-[11px] text-gray-400 mt-2">
              Each run re-evaluates the full rule: first-time matches create NEW
              discrepancies; records that stopped matching auto-resolve as “No
              longer detected”; human fields are never touched.
            </p>
          </div>
        </div>
      )}
    </Drawer>
  );
}

function ReviewRuleConfigView({
  config,
  version,
}: {
  config: ReviewRuleConfig | null;
  version: number;
}) {
  if (!config) return null;

  if (config.tier) {
    return (
      <div className="rounded-md border border-gray-200 p-3 text-xs">
        <div className="mb-1 text-[11px] text-gray-500">
          Response time · v{version}
        </div>
        <p className="m-0">
          {config.tier === "sae" ? "Serious adverse events" : "Routine records"}{" "}
          must be reviewed within{" "}
          <span className="font-semibold">{config.hours} hours</span> of
          arriving in Vivo.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-gray-200 p-3 text-xs">
      <div className="mb-2 flex justify-between text-[11px] text-gray-500">
        <span>Materiality · v{version}</span>
        <span>classifications keep the version that made them</span>
      </div>
      <p className="m-0 mb-1 font-semibold">
        Changing these reopens medical review
      </p>
      <div className="mb-3 flex flex-wrap gap-1">
        {(config.material_fields ?? []).map((field) => (
          <Tag key={field} color="magenta">
            {field}
          </Tag>
        ))}
      </div>
      {(config.administrative_fields ?? []).length > 0 && (
        <>
          <p className="m-0 mb-1 font-semibold">
            Changing these is logged only
          </p>
          <div className="flex flex-wrap gap-1">
            {(config.administrative_fields ?? []).map((field) => (
              <Tag key={field}>{field}</Tag>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
